// ============================================================================
// useHipocampoFlow — MS-09
// ----------------------------------------------------------------------------
// Lê estado das 5 "layers" do Hipocampo OS para o diagrama animado.
//
// Mapeamento real → conceitual:
//   • Working   → consolidated_memories WHERE memory_layer = 'recent'
//   • Short     → consolidated_memories WHERE memory_layer = 'episodic' (24h)
//   • Long      → consolidated_memories WHERE memory_layer IN ('operational')
//                 OU episodic > 24h
//   • People    → syra_person_memory_audit (extrações de fatos por pessoa)
//   • Procedural→ cerebellar_templates (hábitos/skills aprendidos)
//
// Cache TTL + polling configurável + pausa em background (mesmo padrão de
// useTomLive). Expõe `lastUpdatedAt` para FreshnessIndicator e `pulses`,
// uma lista das últimas N atividades por layer (driver da animação).
// ============================================================================

import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export type HipocampoLayer = "working" | "short" | "long" | "people" | "procedural";

export interface HipocampoLayerStat {
  layer: HipocampoLayer;
  total: number;
  last_24h: number;
  lastEventAt: string | null;
}

export interface HipocampoPulse {
  layer: HipocampoLayer;
  at: string; // ISO timestamp
  label: string; // small text shown nearby (memory_type, event tag)
}

export interface HipocampoFlowData {
  layers: Record<HipocampoLayer, HipocampoLayerStat>;
  pulses: HipocampoPulse[];
  loading: boolean;
  error: string | null;
  lastUpdatedAt: Date | null;
  refetch: (opts?: { force?: boolean }) => Promise<void>;
}

interface Snapshot {
  layers: Record<HipocampoLayer, HipocampoLayerStat>;
  pulses: HipocampoPulse[];
  fetchedAt: number;
}

let cache: Snapshot | null = null;
let inflight: Promise<Snapshot> | null = null;
const DEFAULT_CACHE_TTL_MS = 15_000;

function emptyLayers(): Record<HipocampoLayer, HipocampoLayerStat> {
  const layers: HipocampoLayer[] = ["working", "short", "long", "people", "procedural"];
  const out = {} as Record<HipocampoLayer, HipocampoLayerStat>;
  for (const l of layers) out[l] = { layer: l, total: 0, last_24h: 0, lastEventAt: null };
  return out;
}

async function fetchSnapshot(pulseWindowHours = 24): Promise<Snapshot> {
  const since = new Date(Date.now() - 24 * 3600_000).toISOString();
  const pulseSince = new Date(Date.now() - pulseWindowHours * 3600_000).toISOString();
  const layers = emptyLayers();

  // Counts em paralelo: 1 head-count por layer + listagem para pulsos.
  const [
    workingTotal,
    workingDay,
    shortTotal,
    shortDay,
    longTotal,
    longDay,
    peopleTotal,
    peopleDay,
    proceduralTotal,
    proceduralDay,
    workingRecent,
    shortRecent,
    longRecent,
    peopleRecent,
    proceduralRecent,
  ] = await Promise.all([
    // Totals usam count "estimated" (planner stats) — muito mais rápido em
    // tabelas grandes, sem full scan. Janelas 24h continuam exatas.
    // Working = layer 'recent'
    supabase
      .from("consolidated_memories")
      .select("id", { count: "estimated", head: true })
      .eq("active", true)
      .eq("memory_layer", "recent"),
    supabase
      .from("consolidated_memories")
      .select("id", { count: "exact", head: true })
      .eq("active", true)
      .eq("memory_layer", "recent")
      .gte("created_at", since),
    // Short = layer 'episodic'
    supabase
      .from("consolidated_memories")
      .select("id", { count: "estimated", head: true })
      .eq("active", true)
      .eq("memory_layer", "episodic"),
    supabase
      .from("consolidated_memories")
      .select("id", { count: "exact", head: true })
      .eq("active", true)
      .eq("memory_layer", "episodic")
      .gte("created_at", since),
    // Long = operational
    supabase
      .from("consolidated_memories")
      .select("id", { count: "estimated", head: true })
      .eq("active", true)
      .eq("memory_layer", "operational"),
    supabase
      .from("consolidated_memories")
      .select("id", { count: "exact", head: true })
      .eq("active", true)
      .eq("memory_layer", "operational")
      .gte("created_at", since),
    // People = person_memory audit
    supabase.from("syra_person_memory_audit").select("id", { count: "estimated", head: true }),
    supabase
      .from("syra_person_memory_audit")
      .select("id", { count: "exact", head: true })
      .gte("created_at", since),
    // Procedural = cerebellar_templates
    supabase.from("cerebellar_templates").select("id", { count: "estimated", head: true }),
    supabase
      .from("cerebellar_templates")
      .select("id", { count: "exact", head: true })
      .gte("created_at", since),
    // Recents (para pulsos) — filtrados por pulseWindowHours
    supabase
      .from("consolidated_memories")
      .select("memory_type,created_at")
      .eq("active", true)
      .eq("memory_layer", "recent")
      .gte("created_at", pulseSince)
      .order("created_at", { ascending: false })
      .limit(8),
    supabase
      .from("consolidated_memories")
      .select("memory_type,created_at")
      .eq("active", true)
      .eq("memory_layer", "episodic")
      .gte("created_at", pulseSince)
      .order("created_at", { ascending: false })
      .limit(8),
    supabase
      .from("consolidated_memories")
      .select("memory_type,created_at")
      .eq("active", true)
      .eq("memory_layer", "operational")
      .gte("created_at", pulseSince)
      .order("created_at", { ascending: false })
      .limit(8),
    supabase
      .from("syra_person_memory_audit")
      .select("created_at,event_type")
      .gte("created_at", pulseSince)
      .order("created_at", { ascending: false })
      .limit(8),
    supabase
      .from("cerebellar_templates")
      .select("created_at,category")
      .gte("created_at", pulseSince)
      .order("created_at", { ascending: false })
      .limit(8),
  ]);

  layers.working = {
    layer: "working",
    total: workingTotal.count ?? 0,
    last_24h: workingDay.count ?? 0,
    lastEventAt: (workingRecent.data?.[0] as { created_at?: string } | undefined)?.created_at ?? null,
  };
  layers.short = {
    layer: "short",
    total: shortTotal.count ?? 0,
    last_24h: shortDay.count ?? 0,
    lastEventAt: (shortRecent.data?.[0] as { created_at?: string } | undefined)?.created_at ?? null,
  };
  layers.long = {
    layer: "long",
    total: longTotal.count ?? 0,
    last_24h: longDay.count ?? 0,
    lastEventAt: (longRecent.data?.[0] as { created_at?: string } | undefined)?.created_at ?? null,
  };
  layers.people = {
    layer: "people",
    total: peopleTotal.count ?? 0,
    last_24h: peopleDay.count ?? 0,
    lastEventAt: (peopleRecent.data?.[0] as { created_at?: string } | undefined)?.created_at ?? null,
  };
  layers.procedural = {
    layer: "procedural",
    total: proceduralTotal.count ?? 0,
    last_24h: proceduralDay.count ?? 0,
    lastEventAt: (proceduralRecent.data?.[0] as { created_at?: string } | undefined)?.created_at ?? null,
  };

  // Constrói lista de pulsos unificada e ordenada.
  const pulses: HipocampoPulse[] = [];
  for (const r of (workingRecent.data ?? []) as Array<{ memory_type?: string; created_at: string }>) {
    pulses.push({ layer: "working", at: r.created_at, label: r.memory_type ?? "memory" });
  }
  for (const r of (shortRecent.data ?? []) as Array<{ memory_type?: string; created_at: string }>) {
    pulses.push({ layer: "short", at: r.created_at, label: r.memory_type ?? "episodic" });
  }
  for (const r of (longRecent.data ?? []) as Array<{ memory_type?: string; created_at: string }>) {
    pulses.push({ layer: "long", at: r.created_at, label: r.memory_type ?? "operational" });
  }
  for (const r of (peopleRecent.data ?? []) as Array<{ event_type?: string; created_at: string }>) {
    pulses.push({ layer: "people", at: r.created_at, label: r.event_type ?? "person" });
  }
  for (const r of (proceduralRecent.data ?? []) as Array<{ category?: string; created_at: string }>) {
    pulses.push({ layer: "procedural", at: r.created_at, label: r.category ?? "skill" });
  }
  pulses.sort((a, b) => (a.at < b.at ? 1 : -1));

  return { layers, pulses: pulses.slice(0, 30), fetchedAt: Date.now() };
}

export function useHipocampoFlow(
  intervalMs = 0,
  cacheTtlMs = DEFAULT_CACHE_TTL_MS,
  pulseWindowHours = 24,
): HipocampoFlowData {
  const [snap, setSnap] = useState<Snapshot | null>(cache);
  const [loading, setLoading] = useState(!cache);
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(true);
  const windowRef = useRef(pulseWindowHours);
  windowRef.current = pulseWindowHours;

  const refetch = useCallback(
    async (opts?: { force?: boolean }) => {
      const force = opts?.force === true;
      if (!force && cache && Date.now() - cache.fetchedAt < cacheTtlMs) {
        if (mounted.current) setSnap(cache);
        return;
      }
      if (!inflight) {
        inflight = fetchSnapshot(windowRef.current)
          .then((s) => {
            cache = s;
            return s;
          })
          .finally(() => {
            inflight = null;
          });
      }
      if (mounted.current) {
        setLoading(!cache);
        setError(null);
      }
      try {
        const s = await inflight;
        if (mounted.current) setSnap(s);
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        if (mounted.current) setError(msg);
      } finally {
        if (mounted.current) setLoading(false);
      }
    },
    [cacheTtlMs],
  );

  // Refetch when window changes (force, since cache key is single global).
  useEffect(() => {
    cache = null;
    refetch({ force: true });
  }, [pulseWindowHours, refetch]);

  useEffect(() => {
    mounted.current = true;
    refetch();
    return () => {
      mounted.current = false;
    };
  }, [refetch]);

  useEffect(() => {
    if (!intervalMs || intervalMs <= 0) return;
    let timer: ReturnType<typeof setInterval> | null = null;
    const start = () => {
      if (timer) return;
      timer = setInterval(() => {
        if (typeof document !== "undefined" && document.hidden) return;
        refetch();
      }, intervalMs);
    };
    const stop = () => {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    };
    const onVisibility = () => {
      if (document.hidden) stop();
      else {
        refetch();
        start();
      }
    };
    start();
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisibility);
    }
    return () => {
      stop();
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisibility);
      }
    };
  }, [intervalMs, refetch]);

  return {
    layers: snap?.layers ?? emptyLayers(),
    pulses: snap?.pulses ?? [],
    loading,
    error,
    lastUpdatedAt: snap ? new Date(snap.fetchedAt) : null,
    refetch,
  };
}
